import moment from "moment";

import { Button, Radio } from "antd";

import { formatDate } from "utils/helpers";

import FloatLabel from "components/Common/FloatLabel";
import DatePickerLocalised from "components/Common/DatePickerLocalised";

import { ScreeningComponentProps, IcdCodeComponentProps, CptCodeComponentProps, DatepickerComponentProps, MeasureStatusComponentProps, CompletionProgressComponentProps } from './Proptypes';

/**
 * @param props - must pass in Screening or Screening2 otherwise no component is returned (case sensitive)
 * @param screeningType - optional field (used for control blood pressure Systolic, Diastolic)
 * @returns html component
 */
const ScreeningComponent = (props: ScreeningComponentProps) => {
  if (['Screening', 'Screening2'].includes(props.property)) {
    return (
      <>
        <div className="screening-section">
          {!['Diastolic', 'Systolic'].includes(props.screeningType) && (<><label className="control-label label-measure">Select a Screening<span className="red"> (*)</span>:</label><br /></>)}

          {props.screeningType === 'Systolic' && (<><label className="control-label label-measure">Select Systolic Reading<span className="red"> (*)</span>:</label><br /></>)}

          {props.screeningType === 'Diastolic' && (<><label className="control-label label-measure">Select Diastolic Reading<span className="red"> (*)</span>:</label><br /></>)}

          <Radio.Group onChange={(e) => props.setScreeningHandler(e.target.value, props.property)} value={props.property === 'Screening' ? props.measureFormDetails.Screening : props.measureFormDetails.Screening2}>
            {Object.keys(props.measureCodeDict).map((screening: string) => {
              return (
                <>
                  {!['Diastolic', 'Systolic'].includes(props.screeningType) && (
                    <div className="margin-radio-measure">
                      <Radio key={screening} value={screening} disabled={screening.indexOf('does not qualify') > -1 || props.disabled}>{screening}</Radio>
                    </div>
                  )}

                  {(props.screeningType === 'Diastolic' && screening.indexOf('Diastolic') > -1 && (
                    <div className="margin-radio-measure">
                      <Radio key={screening} value={screening} disabled={screening.indexOf('does not qualify') > -1 || props.disabled}>{screening}</Radio>
                    </div>
                  ))}

                  {(props.screeningType === 'Systolic' && screening.indexOf('Systolic') > -1 && (
                    <div className="margin-radio-measure">
                      <Radio key={screening} value={screening} disabled={screening.indexOf('does not qualify') > -1 || props.disabled}>{screening}</Radio>
                    </div>
                  ))}
                </>
              )
            })}
          </Radio.Group>
        </div>
      </>
    )
  } else {
    return (<></>);
  }
}

/**
 * @param props - must pass in IcdCode or IcdCode2 otherwise no component is returned (case sensitive)
 * @returns html component
 */
const IcdCodeComponent = (props: IcdCodeComponentProps) => {
  if (['IcdCode', 'IcdCode2'].includes(props.property)) {
    return (
      <>
        <div className="icdcode-selection">
          <label className="label-measure">Select an ICD Code<span className="red"> (*)</span>:</label><br />
          <Radio.Group onChange={(e) => props.setIcdCodeHandler(e.target.value, props.property)} value={props.property === 'IcdCode' ? props.measureFormDetails.IcdCode : props.measureFormDetails.IcdCode2}>
            {Object.keys(props.measureCodeDict[props.property === 'IcdCode' ? props.measureFormDetails.Screening : props.measureFormDetails.Screening2]).map((icdCode: string) => {
              return (
                <>
                  <div className="margin-radio-measure">
                    <Radio key={icdCode} value={icdCode}>{icdCode}</Radio>
                  </div>
                </>
              )
            })}
          </Radio.Group>
        </div>
      </>
    );
  } else {
    return (<></>);
  }
}

/**
 * @param property - must pass in CptCode or CptCode2 otherwise no component is returned (case sensitive)
 * @returns html component
 */
const CptCodeComponent = (props: CptCodeComponentProps) => {
  if (['CptCode', 'CptCode2'].includes(props.property)) {
    return (
      <>
        <div className="cptCode-selection">
          <label className="label-measure">Select a CPT Code<span className="red"> (*)</span>:</label><br />
          <Radio.Group onChange={(e) => props.setCptCodeHandler(e.target.value, props.property)} value={props.property === 'CptCode' ? props.measureFormDetails.CptCode : props.measureFormDetails.CptCode2}>
            {Object.keys(props.measureCodeDict[props.property === 'CptCode' ? props.measureFormDetails.Screening : props.measureFormDetails.Screening2]
            [props.property === 'CptCode' ? props.measureFormDetails.IcdCode : props.measureFormDetails.IcdCode2]).map((cptCode2: string) => {
              return (
                <>
                  <div className="margin-radio-measure">
                    <Radio key={cptCode2} value={cptCode2}>{cptCode2}</Radio>
                  </div>
                </>
              )
            })}
          </Radio.Group>
        </div>
      </>
    );
  } else {
    return (<></>);
  }
}

const DatepickerComponent = (props: DatepickerComponentProps) => {

  const disabledDate = (current: moment.Moment) => {
    const duration = props.measureFormDetails.RmiFrequencyDuration;
    const frequency = props.measureFormDetails.RmiFrequency;

    // Cannot select days after today and before restrict DOS (RmiFrequency and RmiFrequencyDuration) from API or limit to 1 full year back
    return current > moment().endOf('day') || ((duration && frequency) ? current < (moment().endOf('year').subtract(duration, frequency)) : current < moment().subtract(1, 'year').startOf('year'));
  }

  return (
    <>
      <label className="label-measure">Date of Service<span className="red"> (*)</span>:</label><br />
      <FloatLabel
        label=""
        style={{width: '300px'}}
        value={formatDate( props.measureFormDetails.DateOfService, 'MM/DD/YYYY')}
        name={'dateOfService'}
        form={props.form}
        rules={[
          () => ({
            async validator(_: any, __: any) {
              if (props.measureFormDetails?.DateOfService && !moment(props.measureFormDetails.DateOfService).isValid()) return Promise.reject(new Error('Must be in a correct date format'))
              return Promise.resolve();
            },
          })
        ]}
      >
        <DatePickerLocalised
          date={props.measureFormDetails.DateOfService}
          setDate={(value) => { props.setDatePickerHandler(value); }}
          disabledDate={disabledDate}
          disabled={props.disabled || false}
        />
      </FloatLabel>
    </>
  );
}

/**
 * only completed measures have the measure status section
 */
const MeasureStatusComponent = (props: MeasureStatusComponentProps) => {
  return (
    <div className="measure-status-section">
      <label className="label-measure">Measure Status:</label><br />
      {props.measure?.RmiClaimStatus?.toLowerCase() === 'processing' && ('Processing Claim')}
      {props.measure?.RmiMeasureStatus?.toLowerCase() === 'readytosubmit' && ('Ready to Submit')}
      {props.measure?.RmiClaimStatus?.toLowerCase() === 'completed' && ('Completed')}
    </div>
  );
}

/**
 * In progress and completed measures have this completion section
 */
const CompletionProgressComponent = (props: CompletionProgressComponentProps) => {
  return (
    <>
      <label className="label-measure">Submission Progress:</label> <br />
      {props.measure?.RmiCompletedVisits + ' / ' + props.measure?.RmiFrequencyLimit} submitted
      <div className="save-button">
        <Button
          className="green"
          size="large"
          onClick={() => props.saveMemberMeasure()}
          disabled={
            props.disabled ||
            props.measureFormDetails.Screening === '' ||
            props.measureFormDetails.IcdCode === '' ||
            props.measureFormDetails.CptCode === '' ||
            props.measureFormDetails.DateOfService === '' ||
            props.twoScreenings && (
              props.measureFormDetails.Screening2 === ''  ||
              props.measureFormDetails.IcdCode2 === ''    ||
              props.measureFormDetails.CptCode2 === ''
            )
          }
        >
          Update Screening
        </Button>
      </div>
    </>
  );
}

export {
  ScreeningComponent,
  IcdCodeComponent,
  CptCodeComponent,
  DatepickerComponent,
  MeasureStatusComponent,
  CompletionProgressComponent
};